import { useEffect, useState } from 'react';
import { sendSafetyReport } from '../api/client.js';
import { AlertIcon, CheckIcon } from '../icons.js';

/**
 * Displayed contact address. Deliberately not the backend's recipient: this
 * mailbox is being created, while the button's report goes wherever
 * SAFETY_EMAIL points today.
 */
const SAFETY_EMAIL = 'safety@aslgo.com';

/**
 * The safety message and report form shown below the post-session rating.
 * Self-contained so any rating surface can drop it in with one line. The report
 * is independent of the rating — sending one neither requires nor triggers a
 * star rating.
 */
export function SafetyNotice({ sessionId, peer, onDraftChange }: {
  sessionId: number | null;
  peer: string | null;
  /** Notifies the parent that an unsent report is in progress, so leaving the
   *  screen can warn instead of silently discarding it. */
  onDraftChange?: (hasDraft: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasDraft = open && message.trim().length > 0;
  useEffect(() => { onDraftChange?.(hasDraft); }, [hasDraft, onDraftChange]);

  async function onSend() {
    if (!message.trim()) return;
    setError(null);
    setSending(true);
    try {
      await sendSafetyReport(message, sessionId, peer);
      // Only clear the text once it is safely sent — see the catch below.
      setSent(true);
      setOpen(false);
      setMessage('');
    } catch (e) {
      // Keep `message` and stay open: re-typing an account of being harmed
      // because of a network blip is not acceptable.
      setError((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mt-8 pt-6 border-t border-ink-200 dark:border-white/10 text-left">
      <p className="hint">
        If you felt unsafe, threatened, violated, or disturbed at any time, please contact{' '}
        <a className="underline" href={`mailto:${SAFETY_EMAIL}`}>{SAFETY_EMAIL}</a>.
      </p>

      {sent ? (
        <div className="mt-3 flex flex-col items-start gap-2">
          <p className="inline-flex items-center gap-2 text-[0.9rem] text-status-success">
            <CheckIcon width={16} height={16} />
            Thank you — your report has been sent.
          </p>
          <button
            type="button"
            className="text-[0.9rem] text-ink-600 dark:text-ink-300 hover:underline"
            onClick={() => { setSent(false); setOpen(true); }}
          >
            Report another concern
          </button>
        </div>
      ) : !open ? (
        <button type="button" className="btn-ghost mt-3" onClick={() => setOpen(true)}>
          Report a Safety Concern
        </button>
      ) : (
        <div className="mt-3 flex flex-col gap-3">
          <label className="flex flex-col gap-2">
            <span className="field-label">What happened?</span>
            <textarea
              className="input"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="btn-primary"
              disabled={!message.trim() || sending}
              onClick={() => { void onSend(); }}
            >
              {sending ? 'Sending…' : 'Send report'}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setError(null); }}
              className="text-[0.9rem] text-ink-600 dark:text-ink-300 hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error ? (
        <div className="notice-error mt-3 text-left">
          <AlertIcon className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      ) : null}
    </div>
  );
}
